import { cn } from "@/lib/utils";
import { Info, CheckCircle, AlertTriangle, AlertCircle } from "lucide-react";

type CalloutVariant = "info" | "success" | "warning" | "error";

interface InfoCalloutProps {
  variant?: CalloutVariant;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

const variantStyles: Record<CalloutVariant, string> = {
  info: "bg-blue-50 border-blue-200 text-blue-800",
  success: "bg-green-50 border-green-200 text-green-800",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  error: "bg-red-50 border-red-200 text-red-800",
};

const iconStyles: Record<CalloutVariant, string> = {
  info: "text-blue-600",
  success: "text-green-600",
  warning: "text-amber-600",
  error: "text-red-600",
};

const variantIcons: Record<CalloutVariant, React.ElementType> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

/**
 * InfoCallout - Tinted callout box for contextual help and alerts
 *
 * Variants:
 * - info: Blue — general information and tips
 * - success: Green — positive confirmation messages
 * - warning: Amber — cautions and important notices
 * - error: Red — error messages and critical alerts
 */
export function InfoCallout({ variant = "info", title, children, className }: InfoCalloutProps) {
  const Icon = variantIcons[variant];

  const roleMap: Record<CalloutVariant, string | undefined> = {
    info: undefined,
    success: "status",
    warning: "status",
    error: "alert",
  };

  return (
    <div
      role={roleMap[variant]}
      className={cn("flex gap-3 p-4 border rounded-xl", variantStyles[variant], className)}
    >
      <Icon className={cn("w-5 h-5 shrink-0 mt-0.5", iconStyles[variant])} aria-hidden="true" />
      <div>
        {title && <p className="font-medium mb-1">{title}</p>}
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}
